import styles from 'css/src/components/common/UserOptionsDropdown.module.scss';

import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { RoleEnum, Urls } from 'js/src/client';
import Avatar from 'js/src/components/common/ui/Avatar';
import type { PlainDropdownItem } from 'js/src/components/common/ui/Dropdown';
import { Dropdown, DROPDOWN_DIVIDER } from 'js/src/components/common/ui/Dropdown';
import UserDetailsSection from 'js/src/components/common/UserDetailsSection';
import { getIsUserStaff } from 'js/src/data/account';
import { MODAL_IDS, showModal } from 'js/src/data/modals';
import { HELP_DROPDOWN_OPTIONS } from 'js/src/libs/consts';
import { getUserInitials } from 'js/src/libs/custom-utils';
import { useAppDispatch } from 'js/src/libs/RootState';

type UserOptionsDropdownToggleProps = {
    image?: string;
    avatarAltText?: string;
    onClick: VoidFunction;
    className?: string;
    responsiveIcon?: string;
};

const UserOptionsDropdownToggle = React.memo(function UserDropdownToggle({
    image,
    avatarAltText,
    onClick,
    className,
    responsiveIcon,
    ref,
}: UserOptionsDropdownToggleProps & { ref?: React.Ref<HTMLAnchorElement> }) {
    return (
        <a onClick={onClick} ref={ref} className={classNames(className, 'py-l5-16')}>
            {responsiveIcon ? (
                <i translate="no" className="material-symbols-outlined">
                    {responsiveIcon}
                </i>
            ) : (
                <Avatar image={image} size="small" text={avatarAltText} />
            )}
        </a>
    );
});

type TToggleProps = Readonly<Record<'image' | 'avatarAltText' | 'name' | 'responsiveIcon', string>>;
type TItemProps = PlainDropdownItem<never> & {
    photoUrl?: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    target?: string;
    onClick?: (e: React.MouseEvent) => void;
};
type TItemPropList = readonly TItemProps[];

const USER_DETAILS_ITEM_COMPONENT = UserDetailsSection;

type UserOptionsDropdownProps = {
    role: RoleEnum;
    photoUrl?: string;
    username?: string;
    isResponsive?: boolean;
};

const UserOptionsDropdown = React.memo(function UserOptionsDropdown({
    role,
    photoUrl,
    username,
    isResponsive = false,
}: UserOptionsDropdownProps) {
    const dispatch = useAppDispatch();
    const isStaff = useSelector(getIsUserStaff);

    const computeToggleProps = useCallback(
        (photoUrl = '', username = '', responsiveIcon = ''): TToggleProps => {
            return {
                image: photoUrl,
                avatarAltText: SERVER.USER
                    ? getUserInitials(SERVER.USER.first_name, SERVER.USER.email)
                    : 'user image',
                name: username,
                responsiveIcon,
            };
        },
        [],
    );

    const computeItemPropsList = useCallback(
        (role: RoleEnum, isStaff: boolean): TItemPropList => {
            const onShowFeedbackModal = () =>
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.SUBMIT_FEEDBACK,
                    }),
                );
            const onShowFeatureFlagsModal = () =>
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_FLAGS_MANAGEMENT,
                    }),
                );
            const responsiveOptions: TItemProps[] = [
                HELP_DROPDOWN_OPTIONS.learn,
                HELP_DROPDOWN_OPTIONS.feedback(onShowFeedbackModal),
                HELP_DROPDOWN_OPTIONS.support,
                HELP_DROPDOWN_OPTIONS.help,
            ];

            return [
                {
                    id: 'user-details',
                    text: 'name and/or email',
                    itemComponent: USER_DETAILS_ITEM_COMPONENT,
                    photoUrl: photoUrl,
                    firstName: SERVER.USER ? SERVER.USER.first_name : null,
                    lastName: SERVER.USER ? SERVER.USER.last_name : null,
                    email: SERVER.USER ? SERVER.USER.email : null,
                },
                {
                    id: 'dashboard',
                    href: Urls.DASHBOARD,
                    icon: 'ondemand_video',
                    text: 'All videos',
                },
                {
                    id: DROPDOWN_DIVIDER,
                },
                ...(role === RoleEnum.ADMIN
                    ? [
                          {
                              id: 'workspaces',
                              href: Urls.WORKSPACE_SETTINGS,
                              icon: 'work',
                              text: 'Workspaces',
                          },
                          {
                              id: 'team-members',
                              href: Urls.TEAM_MANAGEMENT,
                              icon: 'people_alt',
                              text: 'Team',
                          },
                          {
                              id: 'billing',
                              href: Urls.BILLING_SETTINGS,
                              icon: 'credit_card',
                              text: 'Billing',
                          },
                      ]
                    : []),
                {
                    id: 'account',
                    href: Urls.ACCOUNT,
                    icon: 'settings',
                    text: 'Account settings',
                },
                ...(role === RoleEnum.ADMIN || role === RoleEnum.EDITOR
                    ? [
                          {
                              id: 'video-settings',
                              href: Urls.VIDEO_SETTINGS,
                              icon: 'video_settings',
                              text: 'Video settings',
                          },
                      ]
                    : []),

                {
                    id: DROPDOWN_DIVIDER,
                },
                ...(isResponsive ? responsiveOptions : []),
                ...(isStaff
                    ? [
                          {
                              id: 'feature-flags',
                              icon: 'flag',
                              text: 'Feature flags [Staff only]',
                              onClick: onShowFeatureFlagsModal,
                          },
                      ]
                    : []),
                {
                    id: 'logout',
                    href: Urls.LOGOUT,
                    icon: 'exit_to_app',
                    text: 'Logout',
                },
            ];
        },
        [dispatch, isResponsive, photoUrl],
    );

    const [toggleProps, setToggleProps] = useState<TToggleProps | undefined>(undefined);
    const [itemPropsList, setItemPropsList] = useState<TItemPropList | undefined>(undefined);
    useEffect(() => {
        setItemPropsList(computeItemPropsList(role, isStaff));
    }, [role, isStaff, computeItemPropsList]);
    useEffect(() => {
        setToggleProps(computeToggleProps(photoUrl, username, isResponsive ? 'menu' : ''));
    }, [computeToggleProps, isResponsive, photoUrl, username]);

    if (
        !SERVER.USER || // used on the share page
        (toggleProps === null && itemPropsList === null) // the first render, these variables are null
    ) {
        return null;
    }

    return (
        <Dropdown
            className={`nav-item ${styles['user-options-dropdown']}`}
            ToggleComponent={UserOptionsDropdownToggle}
            toggleProps={toggleProps}
            items={itemPropsList}
            breakOverflow
        />
    );
});

export default UserOptionsDropdown;
